/**
 * The maneuver baseline currently on screen.
 *
 * A module-level store rather than props because the maneuver views (ManeuverScatter, the tables,
 * Map, TimeSeries) already read their data from globalStore and are never handed rows by their
 * parents — threading a baseline down through Scatter -> per-channel chart would mean touching every
 * one of them to pass something they can read directly.
 *
 * The page owns the selection (see useManeuverBaselineDisplay); this is where it is published.
 */

import { createSignal } from 'solid-js';
import type { ManeuverBaselineFitJson } from '../services/baselinesService';
import type { ManeuverBaselineSeriesRow } from '../utils/maneuverBaselineFit';
import type { ManeuverBaselineTracks } from '../utils/maneuverBaselineTracks';

/** The selected baseline and which of its configs is being drawn. */
const [baselineId, setBaselineId] = createSignal<number | null>(null);
const [baselineName, setBaselineName] = createSignal('');
const [baselineConfig, setBaselineConfig] = createSignal('');
const [baselineColor, setBaselineColor] = createSignal('#ffffff');
const [baselineFit, setBaselineFit] = createSignal<ManeuverBaselineFitJson | null>(null);

/**
 * Bumped on every change to the displayed baseline. Charts fold it into their redraw signature, so
 * re-selecting a different baseline with the same name and colour still repaints.
 */
const [overlayRevision, setOverlayRevision] = createSignal(0);

/**
 * The fit flattened for the current config + event type, one row per covered TWS grid point. Set
 * whenever the selection or the page's event type changes, and read by every surface that needs a
 * baseline value at a given wind speed.
 */
const [baselineSeries, setBaselineSeries] = createSignal<ManeuverBaselineSeriesRow[] | null>(null);

/** True while a specific TWS bin is selected and the baseline has something to draw there. */
const [baselineActive, setBaselineActive] = createSignal(false);

/** TWS = ALL: the map/timeseries overlays are off. The scatter draws the curve in either TWS mode. */
const [baselineScatterAllActive, setBaselineScatterAllActive] = createSignal(false);

/**
 * The averaged member tracks for the selected wind speed — the map path(s) and the timeseries traces.
 *
 * Unlike `baselineSeries` this is fetched, so it arrives after the rest of the overlay. Null until it
 * does, and again whenever the selection cannot produce one.
 */
const [baselineTracks, setBaselineTracks] = createSignal<ManeuverBaselineTracks | null>(null);

/**
 * True while a track load is in flight.
 *
 * The map and timeseries suppress the overlay on this rather than drawing the previous TWS bin's path
 * for a wind speed the user has already left. That makes it a signal every exit path of the loader
 * must clear — a stuck `true` hides the baseline for good rather than merely leaving it stale.
 */
const [baselineLoading, setBaselineLoading] = createSignal(false);

export const maneuverBaselineStore = {
  baselineId,
  setBaselineId,
  baselineName,
  setBaselineName,
  baselineConfig,
  setBaselineConfig,
  baselineColor,
  setBaselineColor,
  baselineFit,
  setBaselineFit,
  overlayRevision,
  bumpOverlayRevision: () => setOverlayRevision((r) => r + 1),
  baselineSeries,
  setBaselineSeries,
  baselineActive,
  setBaselineActive,
  baselineScatterAllActive,
  setBaselineScatterAllActive,
  baselineTracks,
  setBaselineTracks,
  baselineLoading,
  setBaselineLoading,

  /**
   * Drop everything. Called when a page unmounts: the store is shared, so leaving
   * ManeuversHistory for FleetManeuversHistory must not leave the previous page's overlay behind.
   */
  clear() {
    setBaselineId(null);
    setBaselineName('');
    setBaselineConfig('');
    setBaselineFit(null);
    setBaselineSeries(null);
    setBaselineActive(false);
    setBaselineScatterAllActive(false);
    setBaselineTracks(null);
    setBaselineLoading(false);
    setOverlayRevision((r) => r + 1);
  },
};
